/**
 * M1 — first per-act extraction. T2's 4.x scenes are pulled out of the
 * SceneRouter god-switch into a typed registry: a Record keyed by
 * T2Screen IDs. TypeScript catches a missing/extra key as a compile
 * error, so a scenario JSON renumber + a forgotten router update can no
 * longer ship as a silent runtime fallthrough.
 *
 * The registry maps each T2 screen to a render function that takes a
 * SceneRenderContext (the inputs every scene needs: projection state,
 * graph, signer parties, the optional invoice-signer header for T3
 * variants). The hosting SceneRouter calls the function and renders.
 *
 * This file is the prototype for M1; act0/1/2/3/5/6 follow the same
 * pattern in subsequent extractions.
 */

import type { ReactElement } from "react";
import type { EventLogState } from "@/lib/sse/useSSE";
import type { GraphState } from "@/components/web/types";
import type { SignParty } from "@/lib/sign/signerParties";
import type { T2Screen } from "@/lib/scenes/screens";

import { WelcomeBack }              from "@/components/scenes/act4/WelcomeBack";
import { ReorderDraft, CounterOfferAndAccept } from "@/components/scenes/act4/ReorderFlow";
import { LogisticsInsurancePicker } from "@/components/scenes/shared/LogisticsInsurancePicker";
import { DocumentCarousel }         from "@/components/scenes/act2/DocumentCarousel";
import { ReconciliationMatrix }     from "@/components/scenes/act2/ReconciliationMatrix";
import { InvoiceFsmTile }           from "@/components/scenes/act2/InvoiceFsmTile";
import { ComplianceSnapshot }       from "@/components/scenes/act2/ComplianceSnapshot";
import { TariffEstimateTile, TariffFinalTile } from "@/components/scenes/shared/TariffTiles";
import { LcComposer }               from "@/components/scenes/act3/LcComposer";
import { GateCascade }              from "@/components/scenes/act3/GateCascade";
import { LcIssued }                 from "@/components/scenes/act3/LcIssued";
import { SmartLogistics }           from "@/components/scenes/act2/SmartLogistics";
import { InsuranceCertAnchor }      from "@/components/scenes/shared/InsuranceCertAnchor";
import { SettlementSimulation }    from "@/components/scenes/act3/SettlementSimulation";
import { T2Reveal }                 from "@/components/scenes/act4/T2Reveal";
import { BridgeBeat }               from "@/components/scenes/shared/BridgeBeat";
import { LcCompositionProgress }    from "@/components/scenes/shared/LcCompositionProgress";
import { BankerEvidence }           from "@/components/scenes/shared/BankerEvidence";
import { InvestorEvidence }         from "@/components/scenes/shared/InvestorEvidence";
import { CraneIllustration }        from "@/components/scenes/shared/illustrations/CraneIllustration";
import { USER_ACTION }              from "@/lib/userActions/UserActionsContext";
import { lcGateDecisions, pickInvoiceState, scenarioReputation } from "@/components/scene/SceneRouterHelpers";
import { ContractCaptureWithHandshake } from "@/components/scene/ContractCaptureWithHandshake";
import { LcPlacementFeeCta } from "@/components/scenes/act3/LcPlacementFeeCta";

export type SceneRenderContext = {
  ev: EventLogState;
  graph: GraphState;
  signParty: SignParty;
  /** Header line above the invoice id — varies by scenario.
   *  T1/T2: "PINT AE invoice · signed by Aurora's Corp DID"
   *  T3:    "UAE FTA invoice · signed by Gulf Phosphate's Corp DID" */
  invoiceSignerHeader: string;
};

/**
 * T2 scene renderers — one per screen ID. The Record<T2Screen, …> shape
 * forces TypeScript to fail compilation if a screen is added/removed in
 * lib/scenes/screens.ts without a matching renderer here.
 */
export const T2_RENDERERS: Record<T2Screen, (ctx: SceneRenderContext) => ReactElement> = {
  "4.1": () => <WelcomeBack />,
  "4.2": () => <ReorderDraft />,
  "4.3": () => <CounterOfferAndAccept />,
  "4.4": () => (
    <LogisticsInsurancePicker
      incoterms="CIF"
      arrangedBy="Aurora"
      lossPayee="FAB"
      cifBasisUsd={1_500_000}
      interactiveInsurerPick
      hideCompositionBlock
      carriers={[
        { name: "Maersk · Triton",        modality: "Reefer", etaDays: 21, onTimePct: 92, selected: true },
        { name: "MSC · Capricorn",        modality: "Reefer", etaDays: 24, onTimePct: 88, selected: false },
        { name: "CMA CGM · Cape Express", modality: "Reefer", etaDays: 22, onTimePct: 90, selected: false },
      ]}
      insurers={[
        { name: "Mapfre Brasil",  policyTemplate: "Marine Cargo · Institute Clauses (A)", selected: true },
        { name: "Tokio Marine",   policyTemplate: "Marine Cargo · Institute Clauses (A)", selected: false },
        { name: "Generali Brasil",policyTemplate: "Marine Cargo · Institute Clauses (B)", selected: false },
      ]}
    />
  ),
  "4.5": ({ ev, signParty }) => (
    <ContractCaptureWithHandshake
      ev={ev}
      signParty={signParty}
      requireSignCta
      showLcCompositionTrigger
      signCtaActionKey={USER_ACTION.T2_SIGN_CONTRACT}
      lcCompositionActionKey={USER_ACTION.T2_BEGIN_LC_COMPOSITION}
    />
  ),
  "4.6": ({ ev, graph }) => (
    <DocumentCarousel documents={ev.documents} graph={graph} />
  ),
  "4.7": ({ ev }) => <ReconciliationMatrix reconciliation={ev.reconciliation} showEblDescError />,
  "4.8": ({ ev, signParty, invoiceSignerHeader }) => (
    <InvoiceFsmTile
      invoiceState={pickInvoiceState(ev, "INV-2026-0187")}
      invoiceId="INV-2026-0187"
      einvoiceSigner={signParty.einvoice}
      einvoicePdf={signParty.einvoicePdf}
      invoiceSignerHeader={invoiceSignerHeader}
      requireSignCta
      ctaActionKey={USER_ACTION.T2_SIGN_INVOICE}
    />
  ),
  "4.9": ({ ev }) => (
    <ComplianceSnapshot
      snapshot={ev.complianceSnapshot}
      expectedCorridorLabel={"Corridor Portal Único Siscomex — Dubai Mirsal Portal\nBrazil (origin) → UAE (destination)"}
      expectedAuthorities={["Receita Federal (BRL)", "E-invoicing and VAT (UAE)", "Dubai Trade", "MOCCAE Porto", "OFAC + UAE Sanctions (Internal Use Only)", "Compliance Hard Stops (Internal Use Only)", "TravelRule (Internal Use Only)"]}
    />
  ),
  "4.10": () => (
    <div className="flex h-full flex-col gap-3">
      <LcCompositionProgress
        startPct={15}
        targetPct={50}
        label="Integrating estimated Tariff into LC composition"
      />
      <TariffEstimateTile cifUsd={1_500_000} jurisdiction="UAE" hsCode="0207.12" />
      <div className="flex-1 min-h-0 grid place-items-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-orbitra-muted">
          Tariff estimate at LC composition · informational
        </p>
      </div>
    </div>
  ),
  "4.11": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <LcCompositionProgress
        startPct={50}
        targetPct={85}
        label="Integrating contract and invoice data into LC composition"
      />
      <div className="flex-1 min-h-0">
        <LcComposer contract={ev.contract} composer={ev.lcComposer} lcMode tenorAsPackage />
      </div>
      <BankerEvidence variant="lc-composer" amountUsd={1_500_000} bankBadge="FAB" />
    </div>
  ),
  "4.12": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <GateCascade
          decisions={lcGateDecisions(ev, "t2-bank-financed")}
          settlementRef="LC-2026-00187"
        />
      </div>
      <BankerEvidence variant="gate-cascade" amountUsd={1_500_000} bankBadge="FAB" />
      <LcPlacementFeeCta
        feeUsd={850}
        bankBadge="FAB"
        actionKey={USER_ACTION.T2_PAY_LC_PLACEMENT_FEE}
      />
    </div>
  ),
  "4.13": ({ ev, graph }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <LcIssued
          lc={ev.lcIssuance}
          graph={graph}
          showDigitalLcModal
          digitalLcAutoOpenKey={USER_ACTION.T2_VIEW_LC_ISSUED}
        />
      </div>
    </div>
  ),
  "4.14": ({ ev }) => {
    const loaded = ev.logisticsMilestones.some((m) => m.milestone === "Loading");
    return (
      <SmartLogistics
        milestones={ev.logisticsMilestones}
        direction="br-to-ae"
        rightColumnExtra={<CraneIllustration loaded={loaded} />}
      />
    );
  },
  "4.15": ({ ev }) => (
    <InsuranceCertAnchor
      shipmentLoaded={ev.logisticsMilestones.some((m) => m.milestone === "Loading")}
      policyType="Marine cargo · Institute Clauses (A)"
      coverage="≥ 110% CIF · USD 1,650,000"
      insurerName="Mapfre Brasil"
      lossPayee="FAB"
      arrangedBy="Aurora"
      mandatoryUcp28
      viewCertificatePdf="/sign-docs/insurance.pdf"
      viewCertificateRef="POL-2026-0187 · 110% CIF · ICC(A)"
    />
  ),
  "4.16": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="grid flex-1 min-h-0 grid-cols-2 gap-4">
        <TariffFinalTile cifUsd={1_500_000} jurisdiction="UAE" hsCode="0207.12" settled />
        <SettlementSimulation
          legs={ev.settlementLegs}
          threeFieldMatched={ev.threeFieldMatched}
          invoiceMatched={ev.invoiceState["INV-2026-0187"] === "MATCHED"}
        />
      </div>
      <BankerEvidence variant="settlement" amountUsd={1_500_000} bankBadge="FAB" />
    </div>
  ),
  "4.17": ({ ev }) => {
    const t2 = scenarioReputation(ev, "t2-bank-financed");
    return (
      <div className="flex h-full flex-col gap-3">
        <div className="flex-1 min-h-0">
          <T2Reveal alMadar={t2.alMadar} aurora={t2.aurora} />
        </div>
        <InvestorEvidence variant="reveal" notionalUsd={1_500_000} />
      </div>
    );
  },
  "4.18": () => <BridgeBeat variant="b" />,
};
